import clsx from 'clsx';
import type { CSSProperties } from 'react';

import { BaseContainer, type BaseContainerProps } from '@commons/containers/BaseContainer';
import { getResponsiveClasses } from '@commons/helpers/styles';
import type { Alignment, ResponsiveStyle, Spacing } from '@commons/types/styles';

/**
 * Flex container component props.
 *
 * Based on the BaseContainer props.
 */

export type FlexProps = BaseContainerProps & {
  /**
   * The gap between the flex items.
   *
   * Can be a spacing value or a number (applied as an inline style).
   * A responsive object with breakpoints as keys is also supported.
   *
   * @example
   * <Flex gap="md" />
   * <Flex gap={20} />
   * <Flex gap={{ xs: 'sm', lg: 'md' }} />
   */
  gap?: ResponsiveStyle<Spacing> | number;

  /**
   * The wrap property of the flex container.
   *
   * A responsive object with breakpoints as keys is also supported.
   *
   * @example
   * <Flex wrap="wrap" />
   * <Flex wrap={{ xs: 'wrap', lg: 'nowrap' }} />
   */
  wrap?: ResponsiveStyle<'wrap' | 'nowrap' | 'wrap-reverse'>;

  /**
   * The `align-items` property of the flex container.
   *
   * A responsive object with breakpoints as keys is also supported.
   *
   * @example
   * <Flex align="center" />
   * <Flex align={{ xs: 'center', lg: 'flex-start' }} />
   */
  align?: ResponsiveStyle<Alignment | 'stretch'>;

  /**
   * The `justify-content` property of the flex container.
   *
   * A responsive object with breakpoints as keys is also supported.
   *
   * @example
   * <Flex justify="center" />
   * <Flex justify={{ xs: 'center', lg: 'flex-start' }} />
   */
  justify?: ResponsiveStyle<Alignment | 'space-between' | 'space-around' | 'space-evenly'>;

  /**
   * The `flex-direction` property of the flex container.
   *
   * A responsive object with breakpoints as keys is also supported.
   *
   * @example
   * <Flex direction="column" />
   * <Flex direction={{ xs: 'column', lg: 'row' }} />
   */
  direction?: ResponsiveStyle<'row' | 'row-reverse' | 'column' | 'column-reverse'>;
};

/**
 * Flex container component.
 */

export const Flex = (props: FlexProps) => {
  const { gap, wrap, align, justify, direction, className, children, style, ref, ...rest } = props;

  // Preparing the responsive classes.
  // Used when a prop needs to change depending on the breakpoint.
  // i.e. direction = { xs: 'column', lg: 'row' }
  const responsiveClasses = [
    ...getResponsiveClasses('wrap', wrap),
    ...getResponsiveClasses('align', align),
    ...getResponsiveClasses('justify', justify),
    ...getResponsiveClasses('direction', direction),
  ];

  // Adding the gap responsive classes if its value is not a number.
  if (gap && typeof gap !== 'number') {
    responsiveClasses.push(...getResponsiveClasses('gap', gap));
  }

  const classes = clsx('flex', responsiveClasses, className);

  const inlineStyles = {
    gap: gap && typeof gap === 'number' ? `${gap}px` : undefined,
    ...style,
  };

  return (
    <BaseContainer ref={ref} className={classes} style={inlineStyles} {...rest}>
      {children}
    </BaseContainer>
  );
};

/**
 * Flex item component props.
 *
 * Based on the BaseContainer props.
 */

export type FlexItemProps = BaseContainerProps & {
  grow?: CSSProperties['flexGrow'] | boolean;
  shrink?: CSSProperties['flexShrink'] | boolean;
  basis?: CSSProperties['flexBasis'];
};

/**
 * Flex item component.
 *
 * Can be used to easily set `grow`, `shrink` and `basis` properties without CSS.
 * Since its based on the BaseContainer, the spacing props are also available.
 */

export const FlexItem = (props: FlexItemProps) => {
  const { grow, shrink, basis, className, children, ref, ...rest } = props;

  const toNumber = (v?: CSSProperties['flexGrow'] | CSSProperties['flexShrink'] | boolean) =>
    typeof v !== 'undefined' ? (v ? 1 : 0) : v;

  const inlineStyles = {
    flexGrow: toNumber(grow),
    flexShrink: toNumber(shrink),
    flexBasis: basis,
  };

  return (
    <BaseContainer ref={ref} className={className} style={inlineStyles} {...rest}>
      {children}
    </BaseContainer>
  );
};

// Assigning FlexItem to the Flex namespace.

Flex.Item = FlexItem;

export default Flex;
